import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './Home.module.css';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store/store';
import { fetchAds } from '../../features/ads/adsThunks';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../../features/ads/i18nFields';
import { toast } from 'react-toastify';
import { fetchCurrentUser } from '../../features/auth/authThunks';
import loaderStyles from '../../components/NavigationLoader/NavigationLoader.module.css';
import ShimmerImage from '../../components/ShimmerImage/ShimmerImage';
import { IoCalendarOutline } from 'react-icons/io5';
import { AD_IMAGE_PLACEHOLDER } from '../../utils/adImagePlaceholder';

interface AdsFilters {
  limit: number;
  dateFrom?: string;
  dateTo?: string;
  type?: string;
  room_nbr?: number;
  min_nights?: number;
  nbr_child?: number;
}

type CalendarCell = {
  date: Date;
  inCurrentMonth: boolean;
};

type SegmentType = 'tous' | 'maison' | 'appartement' | 'villa' | 'hotel';
type AbortableRequest = {
  abort?: () => void;
};
const DEFAULT_SEGMENT: SegmentType = 'tous';
const INITIAL_LOAD_WATCHDOG_MS = 25000;

const segmentToAdType = (segment: SegmentType | null): string | undefined => {
  if (segment === 'tous') return undefined;
  if (segment === 'maison') return 'Maison';
  if (segment === 'appartement') return 'Appartement';
  if (segment === 'villa') return 'Villa';
  if (segment === 'hotel') return 'Hotel';
  return undefined;
};

const TITLE_MAX_CHARS = 44;
const CalendarIcon = IoCalendarOutline as unknown as React.FC;

const FilterSlidersIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 4h7" stroke="currentColor" strokeWidth="1.36" strokeLinecap="round" />
    <path d="M11.5 4H14" stroke="currentColor" strokeWidth="1.36" strokeLinecap="round" />
    <circle cx="9.5" cy="4" r="1.5" stroke="currentColor" strokeWidth="1.16" fill="white" />
    <path d="M2 8h2.5" stroke="currentColor" strokeWidth="1.36" strokeLinecap="round" />
    <path d="M7 8h7" stroke="currentColor" strokeWidth="1.36" strokeLinecap="round" />
    <circle cx="5.5" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.16" fill="white" />
    <path d="M2 12h7" stroke="currentColor" strokeWidth="1.36" strokeLinecap="round" />
    <path d="M11.5 12H14" stroke="currentColor" strokeWidth="1.36" strokeLinecap="round" />
    <circle cx="9.5" cy="12" r="1.5" stroke="currentColor" strokeWidth="1.16" fill="white" />
  </svg>
);

const truncateWithEllipsis = (text: string, maxChars: number): string => {
  if (!text) return '';
  const normalized = text.trim();
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, maxChars).trimEnd()}...`;
};

const normalizeDate = (value: Date): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const addMonths = (value: Date, months: number): Date =>
  new Date(value.getFullYear(), value.getMonth() + months, 1);

const formatDateKey = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return normalizeDate(parsed);
};

const buildMonthCells = (monthDate: Date): CalendarCell[] => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const firstDayIndex = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPreviousMonth = new Date(year, month, 0).getDate();
  const cells: CalendarCell[] = [];

  for (let i = firstDayIndex - 1; i >= 0; i -= 1) {
    cells.push({
      date: new Date(year, month - 1, daysInPreviousMonth - i),
      inCurrentMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      date: new Date(year, month, day),
      inCurrentMonth: true,
    });
  }

  let trailingDay = 1;
  while (cells.length < 42) {
    cells.push({
      date: new Date(year, month + 1, trailingDay),
      inCurrentMonth: false,
    });
    trailingDay += 1;
  }

  return cells;
};

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const { items, loading, loadingMore, error, hasMore, nextCursor } = useSelector((state: RootState) => state.ads);
  const DEFAULT_LIMIT = 10;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const initialLoadRequestRef = useRef<AbortableRequest | null>(null);
  const lastStartupFetchAtRef = useRef(0);
  const [requestedOnce, setRequestedOnce] = useState(false);
  const [selectedDateFrom, setSelectedDateFrom] = useState('');
  const [selectedDateTo, setSelectedDateTo] = useState('');
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('signupConfirmed') !== '1') return;

    void dispatch(fetchCurrentUser()).finally(() => {
      toast.success(t('home.signupConfirmed'), {
        autoClose: 3000,
        theme: 'colored',
      });
    });

    params.delete('signupConfirmed');
    params.delete('lang');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [dispatch, location.pathname, location.search, navigate, t]);
  const [calendarMode, setCalendarMode] = useState<'single' | 'range'>('single');
  const [calendarDateFromInput, setCalendarDateFromInput] = useState('');
  const [calendarDateToInput, setCalendarDateToInput] = useState('');
  const [calendarError, setCalendarError] = useState('');
  const [calendarVisibleMonth, setCalendarVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calendarHoverDate, setCalendarHoverDate] = useState<string>('');
  const [advancedRoomCount, setAdvancedRoomCount] = useState<number | null>(null);
  const [advancedMinNights, setAdvancedMinNights] = useState<number | null>(null);
  const [advancedKidsCount, setAdvancedKidsCount] = useState<number | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<AdsFilters>({
    limit: DEFAULT_LIMIT,
    type: segmentToAdType(DEFAULT_SEGMENT),
  });
  const [showGoTop, setShowGoTop] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<SegmentType | null>(DEFAULT_SEGMENT);
  const [segmentFilterLoading, setSegmentFilterLoading] = useState(false);
  const today = useMemo(() => normalizeDate(new Date()), []);
  const showInitialLoader = loading && requestedOnce && items.length === 0;
  const firstMonthCells = useMemo(() => buildMonthCells(calendarVisibleMonth), [calendarVisibleMonth]);
  const secondVisibleMonth = useMemo(() => addMonths(calendarVisibleMonth, 1), [calendarVisibleMonth]);
  const secondMonthCells = useMemo(() => buildMonthCells(secondVisibleMonth), [secondVisibleMonth]);
  const weekDayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.resolvedLanguage || 'fr', { weekday: 'short' });
    const mondayBase = new Date(2024, 0, 1); // Monday
    return Array.from({ length: 7 }, (_, idx) => {
      const label = formatter.format(new Date(2024, 0, mondayBase.getDate() + idx));
      return label.replace('.', '');
    });
  }, [i18n.resolvedLanguage]);
  const rangeStartDate = useMemo(() => parseDateKey(calendarDateFromInput), [calendarDateFromInput]);
  const rangeEndDate = useMemo(
    () => parseDateKey(calendarMode === 'single' ? calendarDateFromInput : calendarDateToInput),
    [calendarDateFromInput, calendarDateToInput, calendarMode]
  );
  const isAdvancedFilterApplied = Boolean(
    (appliedFilters.dateFrom && appliedFilters.dateTo) ||
    appliedFilters.room_nbr !== undefined ||
    appliedFilters.min_nights !== undefined ||
    appliedFilters.nbr_child !== undefined
  );
  const canApplyAdvancedFilters = Boolean(
    (selectedDateFrom && selectedDateTo) ||
    advancedRoomCount !== null ||
    advancedMinNights !== null ||
    advancedKidsCount !== null
  );
  const filterTriggerSummary = [
    appliedFilters.room_nbr !== undefined
      ? t('home.mobileRooms', { count: appliedFilters.room_nbr })
      : t('home.defaultRooms'),
    appliedFilters.nbr_child !== undefined
      ? t('home.mobileKids', { count: appliedFilters.nbr_child })
      : t('home.defaultKids'),
    appliedFilters.min_nights !== undefined
      ? t('home.mobileMinNights', { count: appliedFilters.min_nights })
      : t('home.defaultMinNights'),
  ].join(' | ');
  const openCalendarPopup = () => {
    setCalendarDateFromInput(selectedDateFrom);
    setCalendarDateToInput(selectedDateTo);
    setCalendarMode(selectedDateFrom && selectedDateFrom !== selectedDateTo ? 'range' : 'single');
    setCalendarError('');
    setCalendarHoverDate('');
    const initialDate = parseDateKey(selectedDateFrom);
    const monthSource = initialDate || today;
    setCalendarVisibleMonth(new Date(monthSource.getFullYear(), monthSource.getMonth(), 1));
    setAdvancedFiltersOpen(false);
    setCalendarOpen(true);
  };

  const applyCalendarSelection = () => {
    const from = calendarDateFromInput.trim();
    const to = (calendarMode === 'single' ? calendarDateFromInput : calendarDateToInput).trim();

    if (!from || !to) {
      setCalendarError(t('home.calendarDateRequired'));
      return;
    }
    if (from > to) {
      setCalendarError(t('home.calendarInvalidRange'));
      return;
    }

    setSelectedDateFrom(from);
    setSelectedDateTo(to);
    setCalendarError('');
    setCalendarOpen(false);
    setAdvancedFiltersOpen(true);
  };

  const applyAdvancedFilters = () => {
    const nextFilters: AdsFilters = {
      limit: DEFAULT_LIMIT,
      dateFrom: selectedDateFrom || undefined,
      dateTo: selectedDateTo || undefined,
      type: segmentToAdType(selectedSegment),
      room_nbr: advancedRoomCount ?? undefined,
      min_nights: advancedMinNights ?? undefined,
      nbr_child: advancedKidsCount ?? undefined,
    };
    setAppliedFilters(nextFilters);
    dispatch(fetchAds({ ...nextFilters, reset: true }));
    setRequestedOnce(true);
    setAdvancedFiltersOpen(false);
  };

  const clearSelectedCalendarDates = () => {
    setSelectedDateFrom('');
    setSelectedDateTo('');
    setCalendarDateFromInput('');
    setCalendarDateToInput('');
    setCalendarHoverDate('');
    setCalendarError('');
  };

  const resetAdvancedFilters = (closePopup: boolean) => {
    clearSelectedCalendarDates();
    setAdvancedRoomCount(null);
    setAdvancedMinNights(null);
    setAdvancedKidsCount(null);
    const nextFilters: AdsFilters = {
      limit: DEFAULT_LIMIT,
      type: segmentToAdType(selectedSegment),
    };
    setAppliedFilters(nextFilters);
    dispatch(fetchAds({ ...nextFilters, reset: true }));
    setRequestedOnce(true);
    setAdvancedFiltersOpen(!closePopup);
    setCalendarOpen(false);
  };

  const closeAdvancedFiltersPopup = () => {
    setAdvancedFiltersOpen(false);
    setCalendarOpen(false);
    setCalendarError('');
  };

  useEffect(() => {
    initialLoadRequestRef.current?.abort?.();
    lastStartupFetchAtRef.current = Date.now();
    initialLoadRequestRef.current = dispatch(fetchAds({
      limit: DEFAULT_LIMIT,
      type: segmentToAdType(DEFAULT_SEGMENT),
      reset: true,
    }));
    setRequestedOnce(true);

    return () => {
      initialLoadRequestRef.current?.abort?.();
      initialLoadRequestRef.current = null;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!showInitialLoader) return undefined;

    const retryInitialLoad = () => {
      initialLoadRequestRef.current?.abort?.();
      lastStartupFetchAtRef.current = Date.now();
      initialLoadRequestRef.current = dispatch(fetchAds({
        ...appliedFilters,
        limit: appliedFilters.limit || DEFAULT_LIMIT,
        reset: true,
      }));
      setRequestedOnce(true);
    };

    const watchdog = window.setTimeout(retryInitialLoad, INITIAL_LOAD_WATCHDOG_MS);
    return () => window.clearTimeout(watchdog);
  }, [appliedFilters, dispatch, showInitialLoader]);

  useEffect(() => {
    const refreshAfterChromeResume = (force = false) => {
      if (document.visibilityState === 'hidden' || !navigator.onLine) return;
      const recentlyRequested = Date.now() - lastStartupFetchAtRef.current < 3000;
      if (!force && recentlyRequested) return;
      if (!loading && items.length > 0) return;

      initialLoadRequestRef.current?.abort?.();
      lastStartupFetchAtRef.current = Date.now();
      initialLoadRequestRef.current = dispatch(fetchAds({
        ...appliedFilters,
        limit: appliedFilters.limit || DEFAULT_LIMIT,
        reset: true,
      }));
      setRequestedOnce(true);
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      refreshAfterChromeResume(event.persisted);
    };
    const handleVisibilityChange = () => {
      refreshAfterChromeResume();
    };
    const handleOnline = () => {
      refreshAfterChromeResume(true);
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [appliedFilters, dispatch, items.length, loading]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (loading || loadingMore || error || !hasMore || !nextCursor) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          dispatch(fetchAds({ ...appliedFilters, cursor: nextCursor }));
        }
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [dispatch, loading, loadingMore, error, hasMore, nextCursor, appliedFilters]);

  useEffect(() => {
    const onScroll = () => {
      setShowGoTop(window.scrollY > 260);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (loading) return;
    setSegmentFilterLoading(false);
  }, [loading]);

  useEffect(() => {
    if (!calendarOpen) return undefined;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [calendarOpen]);

  const handleSegmentFilter = (target: SegmentType) => {
    const nextSegment: SegmentType = target;
    setSelectedSegment(nextSegment);
    const nextType = segmentToAdType(nextSegment);
    const nextFilters: AdsFilters = {
      limit: DEFAULT_LIMIT,
      dateFrom: appliedFilters.dateFrom || undefined,
      dateTo: appliedFilters.dateTo || undefined,
      type: nextType,
      room_nbr: appliedFilters.room_nbr ?? undefined,
      min_nights: appliedFilters.min_nights ?? undefined,
      nbr_child: appliedFilters.nbr_child ?? undefined,
    };
    setSegmentFilterLoading(true);
    setAppliedFilters(nextFilters);
    dispatch(fetchAds({ ...nextFilters, reset: true }));
    setRequestedOnce(true);
  };

  const handleCalendarDaySelect = (dateValue: Date) => {
    const normalizedDate = normalizeDate(dateValue);
    if (normalizedDate.getTime() < today.getTime()) return;
    const dateKey = formatDateKey(normalizedDate);

    if (calendarMode === 'single') {
      setCalendarDateFromInput(dateKey);
      setCalendarDateToInput(dateKey);
      setCalendarError('');
      return;
    }

    if (!calendarDateFromInput || calendarDateToInput) {
      setCalendarDateFromInput(dateKey);
      setCalendarDateToInput('');
      setCalendarHoverDate('');
      setCalendarError('');
      return;
    }

    if (dateKey < calendarDateFromInput) {
      setCalendarDateFromInput(dateKey);
      setCalendarError('');
      return;
    }

    setCalendarDateToInput(dateKey);
    setCalendarHoverDate('');
    setCalendarError('');
  };

  const formatMonthTitle = (value: Date): string => {
    const title = new Intl.DateTimeFormat(i18n.resolvedLanguage || 'fr', {
      month: 'long',
      year: 'numeric',
    }).format(value);
    return title.charAt(0).toUpperCase() + title.slice(1);
  };

  const adjustCounter = (
    setter: React.Dispatch<React.SetStateAction<number | null>>,
    delta: -1 | 1,
    max: number
  ) => {
    setter((prev) => {
      if (delta === 1) {
        if (prev === null) return 1;
        return prev >= max ? prev : prev + 1;
      }
      if (prev === null) return null;
      if (prev <= 1) return null;
      return prev - 1;
    });
  };

  const renderCalendarMonth = (monthDate: Date, cells: CalendarCell[]) => (
    <div className={styles.travelCalendarMonth}>
      <h4>{formatMonthTitle(monthDate)}</h4>
      <div className={styles.travelWeekRow}>
        {weekDayLabels.map((dayLabel, idx) => (
          <span key={`${monthDate.getMonth()}-${idx}`}>{dayLabel}</span>
        ))}
      </div>
      <div className={styles.travelMonthGrid}>
        {cells.map((cell, idx) => {
          const normalizedDate = normalizeDate(cell.date);
          const dateKey = formatDateKey(normalizedDate);
          const isPast = normalizedDate.getTime() < today.getTime();
          const isDisabled = !cell.inCurrentMonth || isPast;
          const isStart = Boolean(cell.inCurrentMonth && rangeStartDate && formatDateKey(rangeStartDate) === dateKey);
          const isEnd = Boolean(cell.inCurrentMonth && rangeEndDate && formatDateKey(rangeEndDate) === dateKey);
          const hoverRangeEnd = calendarMode === 'range' && !rangeEndDate && calendarHoverDate ? calendarHoverDate : '';
          const inConfirmedRange =
            cell.inCurrentMonth &&
            rangeStartDate &&
            rangeEndDate &&
            dateKey > formatDateKey(rangeStartDate) &&
            dateKey < formatDateKey(rangeEndDate);
          const inHoverRange =
            cell.inCurrentMonth &&
            rangeStartDate &&
            !rangeEndDate &&
            hoverRangeEnd &&
            hoverRangeEnd >= formatDateKey(rangeStartDate) &&
            dateKey > formatDateKey(rangeStartDate) &&
            dateKey < hoverRangeEnd;
          const isInRange = Boolean(inConfirmedRange || inHoverRange);

          const className = [
            styles.travelDay,
            !cell.inCurrentMonth ? styles.travelDayMuted : '',
            isPast ? styles.travelDayPast : '',
            isInRange ? styles.travelDayInRange : '',
            isStart || isEnd ? styles.travelDayPoint : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={`${monthDate.getMonth()}-${idx}`}
              type="button"
              className={className}
              disabled={isDisabled}
              onClick={() => handleCalendarDaySelect(cell.date)}
              onMouseEnter={() => {
                if (calendarMode !== 'range' || !rangeStartDate || rangeEndDate) return;
                if (dateKey >= formatDateKey(rangeStartDate)) setCalendarHoverDate(dateKey);
              }}
            >
              {cell.inCurrentMonth ? cell.date.getDate() : ''}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.topBar}>
          <div className={styles.segment}>
            <button
              className={`${styles.segmentBtn} ${selectedSegment === 'tous' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleSegmentFilter('tous')}
              type="button"
            >
              {t('home.segments.all')}
            </button>
            <button
              className={`${styles.segmentBtn} ${selectedSegment === 'maison' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleSegmentFilter('maison')}
              type="button"
            >
              {t('home.segments.house')}
            </button>
            <button
              className={`${styles.segmentBtn} ${selectedSegment === 'appartement' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleSegmentFilter('appartement')}
              type="button"
            >
              {t('home.segments.apartment')}
            </button>
            <button
              className={`${styles.segmentBtn} ${selectedSegment === 'villa' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleSegmentFilter('villa')}
              type="button"
            >
              {t('home.segments.townhouse')}
            </button>
            <button
              className={`${styles.segmentBtn} ${selectedSegment === 'hotel' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleSegmentFilter('hotel')}
              type="button"
            >
              {t('home.segments.hotel')}
            </button>
          </div>
        </header>

        <section className={styles.filters}>
          <div className={styles.mobileFilterCard}>
            <button
              className={`${styles.searchAction} ${styles.advancedFilterBtn}`}
              onClick={() => {
                setAdvancedFiltersOpen(true);
                setCalendarOpen(false);
                setCalendarError('');
              }}
              type="button"
            >
              <span className={styles.advancedFilterBtnSummary}>{filterTriggerSummary}</span>
              <span className={styles.advancedFilterBtnAction}>
                <span className={styles.advancedFilterBtnIconWrap} aria-hidden="true">
                  <FilterSlidersIcon />
                </span>
                <span className={styles.advancedFilterBtnLabel}>{t('home.openFilters')}</span>
              </span>
            </button>
          </div>
        </section>

        <p className={styles.resultsCount}>
          {t('home.listingsCount', { count: items.length })}
          {segmentFilterLoading ? <span className={styles.resultsCountLoading}> • {t('home.loading')}</span> : null}
        </p>

        <section className={`${styles.grid} ${segmentFilterLoading ? styles.gridRefreshing : ''}`}>
          {loading && <p className={styles.stateText}>{t('home.loading')}</p>}
          {!loading && error && <p className={styles.stateText}>{t('home.error', { error })}</p>}
          {!loading && !error && requestedOnce && items.length === 0 && (
            <p className={styles.stateText}>{t('home.empty')}</p>
          )}
          {!loading && !error && items.map((ad, index) => {
            const localizedTitle = getLocalizedText(ad.title_i18n, i18n.resolvedLanguage || 'fr');
            const localizedAddress = getLocalizedText(ad.adress_i18n, i18n.resolvedLanguage || 'fr', ad.adress || '');
            const normalizedType = String(ad.type || '').trim().toLowerCase();
            const localizedType = t(`adTypes.${normalizedType}`, { defaultValue: ad.type || t('home.segments.apartment') });
            const adRate = Number.isInteger(Number(ad.rate)) && Number(ad.rate) >= 1 && Number(ad.rate) <= 5 ? Number(ad.rate) : null;
            const card = (
              <article className={styles.card}>
                <div className={styles.coverWrap}>
                  <div className={styles.cardBadges}>
                    <span className={styles.badgePrimary}>{localizedType}</span>
                    <span className={styles.badgeSecondary}>{`${ad.area || 0} m2`}</span>
                  </div>
                  <ShimmerImage
                    src={ad.photos?.[0] || AD_IMAGE_PLACEHOLDER}
                    alt={localizedTitle}
                    className={styles.cover}
                  />
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardHead}>
                    <div>
                      <h3 title={localizedTitle}>{truncateWithEllipsis(localizedTitle, TITLE_MAX_CHARS)}</h3>
                      <p className={styles.cardAddress}>{localizedAddress || t('home.unknownAddress')}</p>
                    </div>
                  </div>
                  <h2>
                    {new Intl.NumberFormat(i18n.resolvedLanguage || 'fr').format(Number(ad.price || 0))} {String(ad.currency || '').toUpperCase()}
                  </h2>
                  {adRate ? (
                    <div className={styles.ratingStars} aria-label={t('home.adRatingAria', { rating: adRate })}>
                      {Array.from({ length: 5 }, (_, starIndex) => (
                        <span key={starIndex} className={starIndex < adRate ? styles.ratingStarFilled : styles.ratingStarEmpty} aria-hidden="true">
                          {starIndex < adRate ? '★' : '☆'}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className={styles.meta}>
                    <span>{t('home.metaRooms', { count: ad.room_nbr || 0 })}</span>
                    <span>{ad.area || 0} m2</span>
                  </div>
                </div>
              </article>
            );

            if (!ad.id) {
              return <React.Fragment key={`${localizedTitle}-${index}`}>{card}</React.Fragment>;
            }

            return (
              <Link key={ad.id} to={`/ads/${ad.id}`} className={styles.cardLink}>
                {card}
              </Link>
            );
          })}
          {!loading && !error && hasMore && (
            <div ref={sentinelRef} className={styles.loadingMore}>
              {loadingMore ? t('home.loadingMore') : t('home.scrollToLoad')}
            </div>
          )}
        </section>
      </div>
      <button
        type="button"
        className={`${styles.goTopBtn} ${showGoTop ? styles.goTopVisible : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label={t('home.goTop')}
      >
        ↑
      </button>

      {advancedFiltersOpen && (
        <div className={styles.calendarOverlay} onClick={closeAdvancedFiltersPopup}>
          <div className={`${styles.calendarModal} ${styles.advancedFiltersModal}`} onClick={(event) => event.stopPropagation()}>
            <div className={styles.mobileSheetHandle} aria-hidden="true" />
            <h3 className={styles.advancedPopupTitle}>{t('home.advancedFilters')}</h3>
            <div className={styles.advancedPopupHeaderRow}>
              <button type="button" className={styles.advancedPopupActionBtn} onClick={openCalendarPopup}>
                <CalendarIcon />
                <span>{t('home.mobileCalendarShortcut')}</span>
              </button>
              {(selectedDateFrom && selectedDateTo) && (
                <div className={styles.advancedPopupDateSummary}>
                  <div className={styles.advancedPopupDateCard}>
                    <span>{t('home.calendarFrom')}</span>
                    <strong>{selectedDateFrom}</strong>
                  </div>
                  <div className={styles.advancedPopupDateCard}>
                    <span>{t('home.calendarTo')}</span>
                    <strong>{selectedDateTo}</strong>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelectedCalendarDates}
                    className={styles.advancedPopupCancelText}
                  >
                    {t('home.calendarClearDate')}
                  </button>
                </div>
              )}
            </div>

            <div className={styles.advancedCounters}>
              <div className={styles.advancedCounterRow}>
                <p>{t('home.howManyRooms')}</p>
                <div className={styles.advancedCounterControls}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.advancedCounterControl}
                    onClick={() => adjustCounter(setAdvancedRoomCount, -1, 7)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        adjustCounter(setAdvancedRoomCount, -1, 7);
                      }
                    }}
                  >
                    <div className={`${styles.advancedCounterSign} ${styles.advancedCounterSignMinus}`}>-</div>
                  </div>
                  <span>{advancedRoomCount === null ? '0' : advancedRoomCount}</span>
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.advancedCounterControl}
                    onClick={() => adjustCounter(setAdvancedRoomCount, 1, 7)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        adjustCounter(setAdvancedRoomCount, 1, 7);
                      }
                    }}
                  >
                    <div className={`${styles.advancedCounterSign} ${styles.advancedCounterSignPlus}`}>+</div>
                  </div>
                </div>
              </div>
              <div className={styles.advancedCounterRow}>
                <p>{t('home.howManyKids')}</p>
                <div className={styles.advancedCounterControls}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.advancedCounterControl}
                    onClick={() => adjustCounter(setAdvancedKidsCount, -1, 5)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        adjustCounter(setAdvancedKidsCount, -1, 5);
                      }
                    }}
                  >
                    <div className={`${styles.advancedCounterSign} ${styles.advancedCounterSignMinus}`}>-</div>
                  </div>
                  <span>{advancedKidsCount === null ? '0' : advancedKidsCount}</span>
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.advancedCounterControl}
                    onClick={() => adjustCounter(setAdvancedKidsCount, 1, 5)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        adjustCounter(setAdvancedKidsCount, 1, 5);
                      }
                    }}
                  >
                    <div className={`${styles.advancedCounterSign} ${styles.advancedCounterSignPlus}`}>+</div>
                  </div>
                </div>
              </div>
              <div className={styles.advancedCounterRow}>
                <p>{t('home.minimumNightsFilter')}</p>
                <div className={styles.advancedCounterControls}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.advancedCounterControl}
                    onClick={() => adjustCounter(setAdvancedMinNights, -1, 5)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        adjustCounter(setAdvancedMinNights, -1, 5);
                      }
                    }}
                  >
                    <div className={`${styles.advancedCounterSign} ${styles.advancedCounterSignMinus}`}>-</div>
                  </div>
                  <span>{advancedMinNights === null ? '0' : advancedMinNights}</span>
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.advancedCounterControl}
                    onClick={() => adjustCounter(setAdvancedMinNights, 1, 5)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        adjustCounter(setAdvancedMinNights, 1, 5);
                      }
                    }}
                  >
                    <div className={`${styles.advancedCounterSign} ${styles.advancedCounterSignPlus}`}>+</div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.calendarActions}>
              <button
                type="button"
                onClick={() => {
                  if (isAdvancedFilterApplied) {
                    resetAdvancedFilters(false);
                    return;
                  }
                  closeAdvancedFiltersPopup();
                }}
              >
                {isAdvancedFilterApplied ? t('home.reset') : t('home.cancel')}
              </button>
              <button
                type="button"
                onClick={applyAdvancedFilters}
                disabled={!canApplyAdvancedFilters}
              >
                {t('home.filter')}
              </button>
            </div>
          </div>
        </div>
      )}

      {calendarOpen && (
        <div
          className={`${styles.calendarOverlay} ${styles.calendarOverlayCentered}`}
          onClick={() => {
            setCalendarOpen(false);
            setAdvancedFiltersOpen(true);
          }}
        >
          <div className={`${styles.calendarModal} ${styles.calendarModalCentered}`} onClick={(event) => event.stopPropagation()}>
            <div className={styles.mobileSheetHandle} aria-hidden="true" />
            <h3>{t('home.calendarTitle')}</h3>
            <div className={styles.travelTabs}>
              <button
                type="button"
                className={`${styles.travelTab} ${calendarMode === 'single' ? styles.travelTabActive : ''}`}
                onClick={() => {
                  setCalendarMode('single');
                  if (calendarDateFromInput) setCalendarDateToInput(calendarDateFromInput);
                }}
              >
                {t('home.calendarSingleDate')}
              </button>
              <button
                type="button"
                className={`${styles.travelTab} ${calendarMode === 'range' ? styles.travelTabActive : ''}`}
                onClick={() => setCalendarMode('range')}
              >
                {t('home.calendarDateRange')}
              </button>
            </div>

            <div className={styles.travelSelectionSummary}>
              <div className={styles.travelSelectionCard}>
                <span>{t('home.calendarFrom')}</span>
                <strong>{calendarDateFromInput || '--'}</strong>
              </div>
              <div className={styles.travelSelectionCard}>
                <span>{t('home.calendarTo')}</span>
                <strong>{(calendarMode === 'single' ? calendarDateFromInput : calendarDateToInput) || '--'}</strong>
              </div>
            </div>

            <div className={styles.travelCalendarHeader}>
              <button
                type="button"
                className={styles.travelMonthNav}
                onClick={() => setCalendarVisibleMonth((prev) => addMonths(prev, -1))}
              >
                ‹
              </button>
              <button
                type="button"
                className={styles.travelMonthNav}
                onClick={() => setCalendarVisibleMonth((prev) => addMonths(prev, 1))}
              >
                ›
              </button>
            </div>

            <div className={`${styles.travelCalendarWrap} ${calendarMode === 'single' ? styles.travelCalendarWrapSingle : ''}`}>
              {renderCalendarMonth(calendarVisibleMonth, firstMonthCells)}
              {calendarMode === 'range' && renderCalendarMonth(secondVisibleMonth, secondMonthCells)}
            </div>

            {calendarError && <p className={styles.calendarError}>{calendarError}</p>}

            <div className={styles.calendarActions}>
              <button
                type="button"
                onClick={() => {
                  setCalendarOpen(false);
                  setAdvancedFiltersOpen(true);
                }}
              >
                {t('home.cancel')}
              </button>
              <button type="button" onClick={applyCalendarSelection}>{t('home.apply')}</button>
            </div>
          </div>
        </div>
      )}

      {showInitialLoader && (
        <div className={`${loaderStyles.overlay} ${loaderStyles.active}`} role="status" aria-live="polite">
          <div className={loaderStyles.spinner} aria-hidden="true" />
          <p className={loaderStyles.label}>{t('loader.pageLoading')}</p>
        </div>
      )}
    </div>
  );
};

export default Home;
